import {
	db,
	type FocusSession,
	type MosDB,
	newId,
	type StudySession,
	type Topic,
	type Track,
	type TrackStatus,
} from "./db";
import { focusedMinutes } from "./focus-timer";
import { nextUpdatedAt, save, update } from "./store";
import { addDays, APP_TIMEZONE, localDateKey, weekStart } from "./time";

// Tracks, topics and the study log (ST-1, ST-2). Writes go through Dexie and
// the outbox like every other table.

const trimmed = (v: string, field: string, hi: number) => {
	const t = v.trim();
	if (!t || t.length > hi) throw new RangeError(`${field} must be 1-${hi} characters`);
	return t;
};

export async function createTrack(
	input: { name: string; weekly_target_min?: number },
	database: MosDB = db,
): Promise<Track> {
	const target = input.weekly_target_min ?? 0;
	if (!Number.isInteger(target) || target < 0 || target > 10080)
		throw new RangeError("Weekly target must be 0-10080 minutes");
	const now = nextUpdatedAt();
	return save(
		"tracks",
		{
			id: newId(),
			name: trimmed(input.name, "Name", 50),
			weekly_target_min: target,
			status: "active",
			created_at: now,
			updated_at: now,
			deleted_at: null,
		},
		database,
	);
}

export async function updateTrack(
	id: string,
	patch: Partial<{ name: string; weekly_target_min: number; status: TrackStatus }>,
	database: MosDB = db,
): Promise<Track> {
	const next = { ...patch };
	if (next.name !== undefined) next.name = trimmed(next.name, "Name", 50);
	if (
		next.weekly_target_min !== undefined &&
		(!Number.isInteger(next.weekly_target_min) ||
			next.weekly_target_min < 0 ||
			next.weekly_target_min > 10080)
	)
		throw new RangeError("Weekly target must be 0-10080 minutes");
	return update("tracks", id, next, database);
}

export async function createTopic(
	input: { track_id: string; name: string; milestone?: boolean },
	database: MosDB = db,
): Promise<Topic> {
	const now = nextUpdatedAt();
	return save(
		"topics",
		{
			id: newId(),
			track_id: input.track_id,
			name: trimmed(input.name, "Name", 100),
			milestone: input.milestone ?? false,
			done: false,
			created_at: now,
			updated_at: now,
			deleted_at: null,
		},
		database,
	);
}

export async function updateTopic(
	id: string,
	patch: Partial<{ name: string; milestone: boolean; done: boolean }>,
	database: MosDB = db,
): Promise<Topic> {
	const next = { ...patch };
	if (next.name !== undefined) next.name = trimmed(next.name, "Name", 100);
	return update("topics", id, next, database);
}

/** Logs time studied against a track and optional topic (ST-2). */
export async function logStudy(
	input: {
		track_id: string;
		topic_id?: string | null;
		date?: string;
		minutes: number;
		takeaway?: string;
	},
	database: MosDB = db,
): Promise<StudySession> {
	if (!Number.isInteger(input.minutes) || input.minutes < 1 || input.minutes > 1440)
		throw new RangeError("Minutes must be 1-1440");
	const takeaway = (input.takeaway ?? "").trim();
	if (takeaway.length > 500) throw new RangeError("Takeaway must be at most 500 characters");
	if (input.topic_id) {
		const topic = await database.topics.get(input.topic_id);
		if (!topic || topic.deleted_at || topic.track_id !== input.track_id)
			throw new RangeError("That topic belongs to a different track");
	}
	const now = nextUpdatedAt();
	return save(
		"study_sessions",
		{
			id: newId(),
			track_id: input.track_id,
			topic_id: input.topic_id ?? null,
			date: input.date ?? localDateKey(),
			minutes: input.minutes,
			takeaway,
			created_at: now,
			updated_at: now,
			deleted_at: null,
		},
		database,
	);
}

export type WeekProgress = {
	/** Minutes studied plus minutes focused on the track this ISO week (D59). */
	minutes: number;
	target: number;
	/** 0 to 100, capped; 0 when there is no target. */
	percent: number;
	met: boolean;
};

/**
 * This week's progress on a track. Logged study and finished focus sessions
 * tied to the track both count (D59), so a timed session needs no re-entry.
 */
export function weekProgress(
	track: Pick<Track, "id" | "weekly_target_min">,
	week: string,
	study: Pick<StudySession, "track_id" | "date" | "minutes" | "deleted_at">[],
	focus: Pick<
		FocusSession,
		"track_id" | "started_at" | "ended_at" | "planned_min" | "deleted_at"
	>[],
	timeZone = APP_TIMEZONE,
): WeekProgress {
	const last = addDays(week, 6);
	const inWeek = (day: string) => day >= week && day <= last;
	const logged = study
		.filter((s) => !s.deleted_at && s.track_id === track.id && inWeek(s.date))
		.reduce((sum, s) => sum + s.minutes, 0);
	const focused = focus
		.filter(
			(f) =>
				!f.deleted_at &&
				f.track_id === track.id &&
				f.ended_at &&
				inWeek(localDateKey(new Date(f.started_at), timeZone)),
		)
		.reduce((sum, f) => sum + focusedMinutes(f), 0);
	const minutes = logged + focused;
	const target = track.weekly_target_min;
	return {
		minutes,
		target,
		percent: target ? Math.min(100, Math.round((minutes / target) * 100)) : 0,
		met: target > 0 && minutes >= target,
	};
}

export { weekStart };
